import { useLayoutEffect, useRef, useState } from "react";
import { Button } from "../buttons/Button";
import { Checkbox } from "../inputs/Checkbox";
import {
  copyEncryptedRecoveryBackup,
  downloadPreparedRecoveryBackup,
  sharePreparedRecoveryBackup,
  type PreparedRecoveryBackup,
} from "./recoveryBackupExport";

// A prepared file is saved from a fresh user click, never after async encryption.
export const RecoveryBackupDelivery = ({
  backup,
  accountId,
  disabled,
  doneLabel,
  onDone,
}: {
  backup: PreparedRecoveryBackup;
  accountId?: string;
  disabled: boolean;
  doneLabel: string;
  onDone: () => void | Promise<void>;
}) => {
  const [working, setWorking] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [manual, setManual] = useState(false);
  const [notice, setNotice] = useState("");
  const generation = useRef(0);
  const inFlight = useRef(false);
  useLayoutEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setWorking(false);
    setAttempted(false);
    setConfirmed(false);
    setManual(false);
    setNotice("");
    return () => {
      generation.current += 1;
    };
  }, [backup, accountId]);
  useLayoutEffect(() => {
    if (disabled) {
      generation.current += 1;
      inFlight.current = false;
      setWorking(false);
    }
  }, [disabled]);

  const deliver = async (kind: "share" | "copy" | "download") => {
    if (disabled || inFlight.current) return;
    inFlight.current = true;
    const attempt = ++generation.current;
    setWorking(true);
    setConfirmed(false);
    setNotice("");
    try {
      // These helpers invoke the browser API immediately, before their first await.
      if (kind === "share") {
        const result = await sharePreparedRecoveryBackup(backup);
        if (attempt !== generation.current) return;
        if (result === "cancelled") {
          setNotice("Sharing closed. Try again or copy your encrypted backup.");
          return;
        }
        setNotice("Check that you saved the backup before continuing.");
      } else if (kind === "copy") {
        await copyEncryptedRecoveryBackup(backup);
        if (attempt !== generation.current) return;
        setNotice("Copied. Paste and save it separately from your passphrase.");
      } else {
        downloadPreparedRecoveryBackup(backup);
        setNotice(
          "Check your downloads. If a page opens instead, use Copy encrypted backup."
        );
      }
      setAttempted(true);
    } catch {
      if (attempt !== generation.current) return;
      if (kind === "copy") {
        setManual(true);
        setAttempted(true);
        setNotice(
          "Select the encrypted text below and save it separately from your passphrase."
        );
      } else {
        setNotice(
          "File saving is unavailable here. Use Copy encrypted backup."
        );
      }
    } finally {
      if (attempt === generation.current) {
        inFlight.current = false;
        setWorking(false);
      }
    }
  };
  const busy = disabled || working;
  const finish = async () => {
    if (busy || inFlight.current || !attempted || !confirmed) return;
    inFlight.current = true;
    const attempt = ++generation.current;
    setWorking(true);
    try {
      await onDone();
    } catch {
      if (attempt === generation.current)
        setNotice(
          "Could not continue. Your prepared backup is still available."
        );
    } finally {
      // Allow a fresh deliberate retry if onboarding fails; block double clicks.
      if (attempt === generation.current) {
        inFlight.current = false;
        setWorking(false);
      }
    }
  };
  return (
    <>
      <p className="private-mode-access__hint">
        {backup.shareFile
          ? "Choose Save to Files if offered. Keep this backup separate from your passphrase."
          : "Copy your encrypted backup and save it in a note, separately from your passphrase."}
      </p>
      <Button
        className="button--full button--wrap-label"
        label={
          working
            ? "Opening..."
            : backup.shareFile
              ? "Save or share backup"
              : "Copy encrypted backup"
        }
        disabled={busy}
        aria-busy={working}
        onClick={() => deliver(backup.shareFile ? "share" : "copy")}
      />
      {backup.shareFile && (
        <Button
          className="transparent private-mode-access__link"
          label="Copy encrypted backup"
          disabled={busy}
          onClick={() => deliver("copy")}
        />
      )}
      <Button
        className="transparent private-mode-access__link"
        label="Download file instead"
        disabled={busy}
        onClick={() => deliver("download")}
      />
      {notice && (
        <p className="private-mode-access__hint" role="status">
          {notice}
        </p>
      )}
      {manual && (
        <textarea
          className="private-mode-access__backup-text"
          aria-label="Encrypted backup text"
          readOnly
          value={backup.contents}
          spellCheck={false}
          autoCapitalize="none"
          onFocus={(event) => event.target.select()}
        />
      )}
      <Checkbox
        label="I saved my backup separately."
        labelClassName="condensed"
        checked={confirmed}
        disabled={busy || !attempted}
        onChange={(event) => setConfirmed(event.target.checked)}
      />
      <Button
        className="button--full button--wrap-label"
        label={doneLabel}
        disabled={busy || !attempted || !confirmed}
        onClick={finish}
      />
    </>
  );
};
